<template>
  <Transition name="expand" mode="out-in">
    <div
      :class="`ask-wrapper ask-wrapper-${
        showMore ? 'show-more' : 'show-less'
      } `"
      v-if="displayAskInSearch"
      key="ask-wrapper"
    >
      <AskHeader
        :loading="loading"
        @cancel-ask="handleCancelAsk"
        @close-ask="handleCloseAsk"
      ></AskHeader>
      <AskAnswer
        v-if="isTriggered"
        :answerStream="answer"
        :sources="sources"
        :is-decline="isDecline"
        :loading="loading"
        :is-error="isError"
        :citation-labels="citationLabels"
        :feedback-status="feedbackStatus"
        :end-of-stream="endOfStream"
        @go-to-document="goToDocument"
        @copy-document-url="handleCopyDocumentUrl"
        @retry="handleRetry"
        @cancel-ask="handleCancelAsk"
        @open-sidebar="handleOpenSidebar"
        @update-feedback-status="handleUpdateFeedbackStatus"
        @copy-answer="handleCopyAnswerEvent"
      ></AskAnswer>
      <AskEmptyState
        v-else-if="displayEmptyState"
        :search-input="searchInput"
        :last-question="lastAskQuery.question"
        :is-question="isQuestion"
        :try-out-questions="tryOutQuestions"
        :try-out-questions-loading="tryOutQuestionsLoading"
        @trigger-ask="handleTriggerAsk"
        @latest-search="handleDisplayLastAnswer"
      ></AskEmptyState>
      <div class="show-more" v-if="isTriggered && !loading">
        <div
          class="show-more-button-wrapper"
          @click.prevent.stop="showMore = !showMore"
        >
          {{ $t(showMore ? 'ask.search.show-less' : 'ask.search.show-more') }}
          <font-awesome-icon
            :icon="['fal', `chevron-${showMore ? 'up' : 'down'}`]"
            class="ml-2"
          />
        </div>
      </div>
    </div>

    <div key="ask-button" v-if="!displayAskInSearch" class="open-ask">
      <div
        class="open-icon-wrapper"
        @click.prevent.stop="setDisplayAskInSearch(true)"
      >
        <AiBadge size="large" class="mr-1" text="Ask" />
      </div>
    </div>
  </Transition>
</template>

<script>
import AskAnswer from './AskAnswer.vue';
import AskHeader from './AskHeader.vue';
import AskEmptyState from './AskEmptyState.vue';
import AiBadge from 'components/AI/AiBadge.vue';

import { v4 as uuid } from 'uuid';
import { mapActions, mapGetters } from 'vuex';
import { aiClientStreamParser } from 'utils/aiClientStreamParser';

export default {
  name: 'ask-wrapper',
  components: { AskHeader, AskAnswer, AskEmptyState, AiBadge },
  async mounted() {
    this.tryOutQuestionsLoading = true;
    this.tryOutQuestions = await this.fetchTryOutQuestions({
      lang: this.$i18n.locale,
      n: 3,
    });
    this.tryOutQuestionsLoading = false;
  },
  data() {
    return {
      query: {},
      options: {
        includeTypes: ['answer', 'answer-type', 'documents', 'meta'],
        v2: true,
      },
      loading: false,
      sources: [],
      answer: '',
      question: '',
      citationLabels: {},
      isDecline: false,
      isError: false,
      isTriggered: false,
      showMore: true,
      feedbackStatus: '',
      triggerSource: '',
      tryOutQuestions: [],
      tryOutQuestionsLoading: false,
      endOfStream: false,
    };
  },
  props: {
    searchInput: {
      type: String,
      default: '',
    },
    isQuestion: { type: Boolean, default: false },
  },
  computed: {
    displayEmptyState() {
      return (
        (this.lastAskQuery.question && this.lastAskQuery.question.length > 0) ||
        this.tryOutQuestions.length ||
        this.tryOutQuestionsLoading ||
        this.isQuestion
      );
    },
    linkIds() {
      return this.sources ? [...this.sources.map((source) => source.id)] : null;
    },
    ...mapGetters('aiSearchModule', [
      'displayAskInSearch',
      'lastAskQuery',
      'hasPreviousAskQuery',
    ]),
  },
  methods: {
    resetAsk() {
      this.answer = '';
      this.sources = [];
      this.isDecline = false;
      this.isTriggered = false;
      this.loading = false;
      this.showMore = true;
      this.isError = false;
      this.query = {};
      this.question = '';
      this.citationLabels = {};
      this.feedbackStatus = '';
      this.triggerSource = '';
      this.endOfStream = false;
    },
    handleCloseAsk() {
      this.resetAsk();
      this.setDisplayAskInSearch(false);
    },
    handleCancelAsk() {
      this.resetAsk();
    },
    getCorrelationId() {
      return 'web-' + uuid();
    },
    async fetchStream(triggerSource) {
      try {
        for await (const chunk of await this.fetchAsk({
          queryId: this.query.queryId,
          queryBody: this.query.query,
          dev: false,
          askInterface: 'admin-search',
        })) {
          for (let usableChunk of aiClientStreamParser(chunk)) {
            if (usableChunk && usableChunk.content) {
              if (usableChunk.type === 'documents') {
                this.sources = chunk.content;
              }
              if (
                usableChunk.type === 'answer-type' &&
                chunk.content === 'decline'
              )
                this.isDecline = true;
              if (usableChunk.type === 'answer') {
                if (
                  usableChunk.isLink &&
                  usableChunk.sourceId &&
                  usableChunk.linkId &&
                  this.isNumeric(usableChunk.linkId)
                ) {
                  const copy = { ...this.citationLabels };
                  copy[usableChunk.sourceId] = usableChunk.linkId;
                  this.citationLabels = copy;
                }
                this.loading = false;
                this.answer += usableChunk.content;
              }
              if (usableChunk.type === 'meta') {
                this.loading = false;
              }
            }
          }
          this.$emit('update-ask-status', {
            isDecline: this.isDecline,
            isError: this.isError,
            queryId: this.query.queryId,
            answer: this.answer,
            links: this.sources,
            trigger: triggerSource,
            source: 'search',
          });
          if (this.isError) return;
          this.triggerSetLastAskQuery();
        }

        // EVENT

        this.$services.events.ask.answer({
          queryId: this.query.queryId,
          index: 1,
          answer: this.answer,
          links: this.linkIds,
          source: 'search',
          trigger: triggerSource,
        });
        this.endOfStream = true;
      } catch (e) {
        this.loading = false;
        this.isError = true;
        this.answer = this.$t('ask.search.error-message');
        // EVENT

        this.$services.events.ask.error({
          queryId: this.query.queryId,
          index: 0,
          answer: this.answer,
          links: this.linkIds,
          errorMessage: String(e),
          source: 'search',
          trigger: triggerSource,
        });
      }
    },
    isNumeric(str) {
      if (typeof str !== 'string') return false;
      return /^[0-9]*$/.test(str);
    },
    triggerSetLastAskQuery() {
      this.setLastAskQuery({
        question: this.question,
        answer: {
          answerStream: this.answer,
          sources: this.sources,
          citationLabels: this.citationLabels,
          isDecline: this.isDecline,
          query: this.query,
          feedbackStatus: this.feedbackStatus,
        },
      });
    },
    handleRetry() {
      this.handleTriggerAsk({
        question: this.question,
        triggerSource: 'retry',
      });
    },

    handleTriggerAsk(e) {
      const queryId = this.getCorrelationId();

      try {
        this.resetAsk();
        this.question = e.question;
        if (e.triggerSource === 'try_out_question')
          this.$emit('update-search-input', e.question);
        this.isTriggered = true;
        this.loading = true;
        this.triggerSource = e.triggerSource;
        //EVENT
        this.$services.events.ask.question({
          queryText: this.question,
          queryId,
          index: 0,
          source: 'search',
          trigger: e.triggerSource,
        });

        this.query = {
          query: {
            queryText: e.question,
            ...this.options,
          },
          createdAt: new Date(),
          queryId: queryId,
        };
        this.fetchStream(e.triggerSource);
      } catch (e) {
        this.isError = true;
        this.answer = this.$t('ask.search.error-message');
        this.loading = false;

        // EVENT
        this.$services.events.ask.error({
          queryId,
          index: 0,
          answer: this.answer,
          links: this.linkIds,
          errorMessage: e,
          source: 'search',
          trigger: e.triggerSource,
        });
      }
    },
    handleDisplayLastAnswer() {
      const { answer, question } = this.lastAskQuery;
      const {
        answerStream,
        sources,
        citationLabels,
        isDecline,
        query,
        feedbackStatus,
      } = answer;
      this.$emit('update-search-input', question);
      this.$nextTick(() => {
        this.question = question;
        this.isTriggered = true;
        this.loading = false;
        this.answer = answerStream;
        this.sources = sources;
        this.citationLabels = citationLabels;
        this.isDecline = isDecline;
        this.query = query;
        this.feedbackStatus = feedbackStatus;
        this.triggerSource = 'last-search';
        this.$emit('update-ask-status', {
          isDecline: this.isDecline,
          isError: this.isError,
          queryId: this.query.queryId,
          answer: this.answer,
          links: this.sources,
          trigger: this.triggerSource,
          source: 'search',
        });
      });
    },
    goToDocument({ entityId, entityType }) {
      this.handleSourceUseEvent({ sourceId: entityId, trigger: 'full-page' });
      this.$emit('go-to-document', { id: entityId, type: entityType });
    },
    handleCopyDocumentUrl({ entityId, entityType }) {
      this.handleSourceUseEvent({ sourceId: entityId, trigger: 'copy' });
      this.$emit('copy-document-url', { id: entityId, type: entityType });
    },
    handleOpenSidebar(e) {
      this.handleSourceUseEvent({ sourceId: e.entityId, trigger: 'sidebar' });
      this.$emit('open-sidebar', e);
    },
    handleUpdateFeedbackStatus(status) {
      this.feedbackStatus = status;
      this.sendAskFeedback({
        queryId: this.query.queryId,
        queryBody: this.query.query,
        feedback: {
          feedbackType: null,
          rating: status,
          content: null,
        },
      });
      this.triggerSetLastAskQuery();
      this.$services.events.ask.feedback(
        {
          queryId: this.query.queryId,
          queryText: this.question,
          index: 0,
          source: 'search',
          trigger: this.triggerSource,
          feedback: {
            feedbackType: '',
            rating: status,
            content: '',
          },
        },
        window.location.href,
      );
    },
    handleSourceUseEvent({ sourceId, trigger }) {
      this.$services.events.ask.useSource({
        queryId: this.query.queryId,
        index: 0,
        sourceId,
        trigger,
        infos: {
          answer: this.answer,
          links: this.linkIds,
          source: 'search',
          trigger: this.triggerSource,
        },
      });
    },
    handleCopyAnswerEvent() {
      this.$services.events.ask.copyAnswer({
        queryId: this.query.queryId,
        index: 0,
        answer: this.answer,
        source: 'search',
        trigger: this.triggerSource,
      });
    },
    ...mapActions('aiSearchModule', [
      'fetchAsk',
      'sendAskFeedback',
      'setDisplayAskInSearch',
      'setLastAskQuery',
      'fetchTryOutQuestions',
    ]),
    ...mapActions('knowledgeModule', ['goToTranslatedEntity']),
  },
  watch: {
    searchInput(newVal) {
      if (newVal !== this.question) this.resetAsk();
    },
  },
};
</script>

<style lang="scss" scoped>
.ask-wrapper {
  background-color: $purple-2-mayday;
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease-in-out;
  &__expanded {
    max-height: 1000px;
  }
}
.ask-wrapper-show-less {
  max-height: 150px;
}
.ask-wrapper-show-more {
  max-height: 400px;
}
.open-ask {
  background-color: white;
  padding: 10px 24px 0px 24px;
  display: flex;
  justify-content: right;
  transition: all 0.5s ease-in-out;
}
.open-icon-wrapper {
  height: 24px;
  width: 24px;
  color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    cursor: pointer;
  }
}

.expand-enter-active,
.expand-leave-active,
.expand {
  transition: all 0.5s ease-in-out;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  &.ask-wrapper,
  &.open-ask {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
  }
}

.expand-enter-to {
  max-height: 400px;
}

.show-more-button-wrapper {
  width: fit-content;
  height: 25px;
  position: absolute;
  z-index: 1;
  top: 10px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid $purple-5-mayday;
  color: $purple-5-mayday;
  padding: 8px 5px 8px 5px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.show-more {
  display: flex;
  justify-content: center;
  position: relative;
  &:hover .show-more-button-wrapper {
    color: white;
    background-color: $purple-5-mayday;
    cursor: pointer;
  }
}
</style>
